import React from 'react';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { DateRange } from '../types';
import { RangeDay } from './RangeDay';

interface TwoMonthRangeProps {
  leftMonth: Dayjs;
  setLeftMonth: (m: Dayjs) => void;
  range: DateRange;
  onSelectDay: (d: Dayjs) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

export function TwoMonthRange({
  leftMonth,
  setLeftMonth,
  range,
  onSelectDay,
  minDate,
  maxDate,
}: TwoMonthRangeProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const rightMonth = React.useMemo(
    () => leftMonth.add(1, 'month'),
    [leftMonth],
  );

  const commonProps = {
    reduceAnimations: true,
    showDaysOutsideCurrentMonth: isMobile,
    minDate,
    maxDate,
    slots: { day: (p: any) => <RangeDay {...p} range={range} /> },
    onChange: (d: Dayjs | null) => d && onSelectDay(d),
  } as const;

  return (
    <Stack>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={isMobile ? 1 : 2}
        sx={{ alignItems: 'center' }}
      >
        <DateCalendar
          key={`left-${leftMonth.format('YYYY-MM')}`}
          referenceDate={leftMonth}
          {...commonProps}
          onMonthChange={(newMonth) => setLeftMonth(newMonth)}
          onYearChange={(newYear) =>
            setLeftMonth(leftMonth.year(newYear.year()))
          }
          value={null}
        />
        <Divider orientation="vertical" flexItem />
        {!isMobile && (
          <DateCalendar
            key={`right-${rightMonth.format('YYYY-MM')}`}
            referenceDate={rightMonth}
            {...commonProps}
            onMonthChange={(newMonth) =>
              setLeftMonth(newMonth.subtract(1, 'month'))
            }
            onYearChange={(newYear) =>
              setLeftMonth(leftMonth.year(newYear.year()))
            }
            value={null}
          />
        )}
      </Stack>
      <Divider flexItem />
    </Stack>
  );
}
